// v2/frontend/src/services/api.ts
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { createElement } from 'react';
import { toast } from 'sonner';

// Extend InternalAxiosRequestConfig to include _retry flag
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const api: AxiosInstance = axios.create({
  baseURL: '/api/v2',
  headers: {
    'Content-Type': 'application/json',
  },
});

// =============================================================================
// Pont vers le store d'authentification
// -----------------------------------------------------------------------------
// authStore importe déjà `api` : pour éviter un cycle d'import, le store
// s'enregistre ici au chargement (registerTokenRefresher) et l'intercepteur
// lit/écrit les jetons à travers ce pont. Sans pont enregistré (cas limite),
// on se rabat sur la copie persistée dans localStorage.
// =============================================================================

export interface TokenRefresher {
  /** Jeton d'accès courant (source de vérité : le store Zustand). */
  getAccessToken: () => string | null;
  /** Jeton de rafraîchissement courant. */
  getRefreshToken: () => string | null;
  /** Appelé avec le nouveau jeton d'accès après un rafraîchissement réussi. */
  onAccessTokenRefreshed: (accessToken: string) => void;
  /** Appelé quand le rafraîchissement échoue définitivement (session à purger). */
  onRefreshFailed: () => void;
}

let tokenRefresher: TokenRefresher | null = null;

export function registerTokenRefresher(refresher: TokenRefresher): void {
  tokenRefresher = refresher;
}

interface PersistedAuthState {
  accessToken?: string | null;
  refreshToken?: string | null;
}

function readPersistedAuthState(): PersistedAuthState | null {
  try {
    const raw = localStorage.getItem('veloce-auth');
    if (!raw) return null;
    const { state } = JSON.parse(raw);
    return (state as PersistedAuthState) ?? null;
  } catch {
    return null;
  }
}

function getAccessToken(): string | null {
  return tokenRefresher?.getAccessToken() ?? readPersistedAuthState()?.accessToken ?? null;
}

function getRefreshToken(): string | null {
  return tokenRefresher?.getRefreshToken() ?? readPersistedAuthState()?.refreshToken ?? null;
}

// Promesse de rafraîchissement en cours : N réponses 401 simultanées → un seul
// POST /auth/refresh, tous les appelants attendent le même résultat.
let refreshInFlight: Promise<string> | null = null;

/**
 * Rafraîchit le jeton d'accès (mutualisé : un seul appel réseau à la fois).
 * Résout avec le nouveau jeton ; rejette si aucun jeton de rafraîchissement
 * n'est disponible ou si le serveur refuse.
 */
export function refreshAccessToken(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error('NO_REFRESH_TOKEN');
      }

      // Use the raw axios (not the `api` instance) on purpose: routing the
      // refresh call through `api` would re-enter the response interceptor if
      // the refresh endpoint itself 401s. Keep it raw.
      const response = await axios.post('/api/v2/auth/refresh', { refreshToken });
      const { accessToken } = response.data.data as { accessToken: string };

      if (tokenRefresher) {
        // Le store (persisté) est la source de vérité : il réécrit localStorage lui-même
        tokenRefresher.onAccessTokenRefreshed(accessToken);
      } else {
        const state = readPersistedAuthState();
        if (state) {
          localStorage.setItem('veloce-auth', JSON.stringify({ state: { ...state, accessToken } }));
        }
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      return accessToken;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function handleRefreshFailure(): void {
  if (tokenRefresher) {
    tokenRefresher.onRefreshFailed();
  } else {
    localStorage.removeItem('veloce-auth');
  }
  window.location.href = '/login';
}

/** Un 401 sur ces routes est une réponse métier (mauvais mot de passe…), pas un jeton expiré. */
function isAuthEndpoint(url: string | undefined): boolean {
  return !!url && (url.includes('/auth/login') || url.includes('/auth/refresh'));
}

// =============================================================================
// Intercepteurs
// =============================================================================

// Request interceptor to add auth token and location header
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Jeton lu depuis le store (repli : localStorage)
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Get location from store
  const locationData = localStorage.getItem('veloce-location');
  if (locationData) {
    try {
      const { state } = JSON.parse(locationData);
      if (state?.currentLocation?.id) {
        config.headers['X-Location-Id'] = state.currentLocation.id;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  return config;
});

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig | undefined;

    // Téléchargements binaires (responseType 'blob') : en cas d'erreur, le
    // serveur répond quand même en JSON (message, code, errorId). Le relire
    // pour que toastApiError puisse dire quoi / pourquoi / la référence.
    const blob = error.response?.data;
    if (blob instanceof Blob && /json/i.test(blob.type)) {
      try {
        error.response!.data = JSON.parse(await blob.text()) as unknown;
      } catch {
        /* corps illisible : on garde le Blob, l'indice de statut s'affichera */
      }
    }

    // If 401 and not already retrying
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url)
    ) {
      originalRequest._retry = true;

      if (getRefreshToken()) {
        try {
          const accessToken = await refreshAccessToken();

          // Retry original request
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          handleRefreshFailure();
          return Promise.reject(refreshError);
        }
      }
    }

    // Toast générique pour les erreurs que personne ne rattrape forcément
    // (droits, introuvable, serveur, réseau). L'appelant qui connaît l'action
    // rappelle toastApiError avec elle : même identifiant, le toast est
    // précisé, jamais doublé.
    if (isApiErrorToasted(error)) toastApiError(error);

    return Promise.reject(error);
  }
);

// =============================================================================
// Aides pour les appelants
// =============================================================================

// -----------------------------------------------------------------------------
// Règle 19 (CLAUDE.md) : une erreur guide l'utilisateur ET le développeur.
// Titre = ce qui a échoué ; description = pourquoi et quoi faire (message du
// serveur, sinon explication par statut) ; ligne technique = méthode, route,
// statut, code et référence du journal serveur.
// -----------------------------------------------------------------------------

/** Ce que chaque statut veut dire pour la personne devant l'écran, et quoi faire. */
const STATUS_HINTS: Record<number, string> = {
  400: 'La demande est mal formée ou incomplète — rechargez la page et réessayez ; si ça persiste, c’est un défaut à signaler.',
  401: 'Votre session a expiré : reconnectez-vous.',
  403: 'Vous n’avez pas les droits pour cette action (un compte gestionnaire est souvent requis).',
  404: 'L’élément demandé n’existe plus, ou n’appartient pas à cet établissement : rechargez la page.',
  409: 'Une autre opération est en cours ou l’état a changé entre-temps : attendez sa fin, rechargez, puis réessayez.',
  413: 'Le fichier ou le contenu envoyé est trop volumineux.',
  422: 'La demande est refusée dans l’état actuel des données (voir le détail).',
  429: 'Trop de demandes en peu de temps : patientez quelques secondes.',
  500: 'Erreur interne du serveur : réessayez ; si elle se reproduit, transmettez la référence ci-dessous.',
  502: 'Le serveur, ou un service qu’il appelle, n’a pas répondu correctement : réessayez dans un instant.',
  503: 'Le serveur redémarre ou est surchargé : réessayez dans quelques secondes.',
  504: 'Le serveur n’a pas répondu à temps. L’opération continue peut-être en arrière-plan : attendez une minute, rechargez la page, puis vérifiez avant de recommencer.',
};
const NETWORK_HINT = 'Impossible de joindre le serveur : réseau coupé, ou serveur en cours de redémarrage. Réessayez dans quelques secondes.';

export interface ApiErrorDescription {
  /** Ce qui a échoué (l'action), ou à défaut la cause. */
  title: string;
  /** Pourquoi et quoi faire, puis la ligne technique pour le développeur. */
  description: string;
  status?: number;
  code?: string;
  errorId?: string;
}

function serverPayload(error: unknown): { message?: string; code?: string; errorId?: string; details: string[] } {
  if (!axios.isAxiosError(error)) return { details: [] };
  const data = error.response?.data as { message?: unknown; error?: unknown; errorId?: unknown; details?: unknown } | undefined;
  const message = typeof data?.message === 'string' && data.message.trim() ? data.message.trim() : undefined;
  const code = typeof data?.error === 'string' && data.error ? data.error : undefined;
  const errorId = typeof data?.errorId === 'string' && data.errorId ? data.errorId : undefined;
  const details = Array.isArray(data?.details)
    ? data.details.map((d) => {
      if (d && typeof d === 'object' && 'message' in d) {
        const row = d as { field?: unknown; message?: unknown };
        return `${typeof row.field === 'string' && row.field ? `${row.field} : ` : ''}${String(row.message ?? '')}`;
      }
      return String(d);
    }).filter(Boolean)
    : [];
  return { message, code, errorId, details };
}

/** Décrit une erreur pour l'utilisateur (et le développeur), avec ou sans l'action qui l'a produite. */
export function describeApiError(error: unknown, action?: string): ApiErrorDescription {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const network = !error.response;
    const { message, code, errorId, details } = serverPayload(error);
    const why = message ?? (network ? NETWORK_HINT : (status ? STATUS_HINTS[status] : undefined)) ?? 'Une erreur est survenue.';
    const method = (error.config?.method ?? 'get').toUpperCase();
    const url = error.config?.url ?? '';
    const technical = [
      `${method} ${url}`.trim(),
      network ? (error.code ?? 'réseau') : `HTTP ${status}${code ? ` ${code}` : ''}`,
      errorId ? `réf. ${errorId}` : null,
    ].filter(Boolean).join(' → ');
    const lines = action ? [why, ...details, technical] : [...details, technical];
    return { title: action ?? why, description: lines.join('\n'), status, code, errorId };
  }
  if (error instanceof Error) {
    return { title: action ?? error.message, description: action ? error.message : '' };
  }
  return { title: action ?? 'Une erreur est survenue.', description: '' };
}

/**
 * Affiche l'erreur : titre + description (pourquoi, quoi faire, ligne
 * technique). Le même toast est réutilisé par l'intercepteur et par
 * l'appelant qui précise l'action — jamais deux toasts pour une erreur.
 */
export function toastApiError(error: unknown, action?: string): void {
  const described = describeApiError(error, action);
  const tagged = error as { __toastId?: string | number };
  if (tagged && typeof tagged === 'object' && !tagged.__toastId) {
    tagged.__toastId = `api-error-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  toast.error(described.title, {
    id: tagged?.__toastId,
    description: described.description
      ? createElement('span', { style: { whiteSpace: 'pre-line' } }, described.description)
      : undefined,
    duration: described.status && described.status >= 500 ? 15000 : 10000,
  });
}

/**
 * Message d'erreur lisible pour l'utilisateur (texte inline, sans toast) :
 * le message du serveur, sinon l'explication du statut, sinon le repli.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const { message, errorId } = serverPayload(error);
    const status = error.response?.status;
    const why = message ?? (!error.response ? NETWORK_HINT : (status ? STATUS_HINTS[status] : undefined)) ?? error.message ?? fallback;
    return errorId && !why.includes(errorId) ? `${why} (réf. ${errorId})` : why;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Vrai si l'intercepteur ci-dessus a déjà affiché un toast pour cette erreur
 * (403, 404, 5xx, réseau) — l'appelant ne doit alors pas en afficher un second.
 */
export function isApiErrorToasted(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === 403 || status === 404 || (status !== undefined && status >= 500)) return true;
  return !error.response && error.code === 'ERR_NETWORK';
}

export default api;
